import { useLazyQuery } from '@apollo/client';
import { useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import { PaymentOption } from 'src/app/shared/constants/paymentOptions';
import {
  HIRER_PURCHASE_TYPE_CONTRACT,
  HIRER_PURCHASE_TYPE_CASUAL,
} from 'src/app/shared/constants/tealium';
import { getContractType } from 'src/app/utils/getContractType';
import { getPricingType } from 'src/app/utils/getPricingType';
import { log } from 'src/app/utils/logging';
import { actionUpdatePaymentOption } from 'src/app/v1/state/modules/formData/paymentOption';
import { actionUpdateContractBalance } from 'src/app/v1/state/modules/session/contractBalance';
import { actionUpdateAdvertiser } from 'src/app/v1/state/modules/tealium/advertiser';
import { actionUpdateHirerContract } from 'src/app/v1/state/modules/tealium/hirerContract';
import { actionUpdateHirerPurchaseType } from 'src/app/v1/state/modules/tealium/hirerPurchaseType';
import { actionUpdatePaymentMethod } from 'src/app/v1/state/modules/tealium/paymentMethod';
import { GET_CONTRACT_BALANCE } from 'src/graphql/contracts';
import type { AppState } from 'src/types/appState';
import { TealiumContractType } from 'src/types/tealium';

export const useContractBalance = () => {
  const { advertiserNation, pricingModel, seekAdvertiserId } = useSelector(
    (state: AppState) => ({
      advertiserNation: state.session.hirerAccount.nation,
      pricingModel: state.session.hirerAccount.pricingModel ?? undefined,
      seekAdvertiserId: state.session.seekAdvertiserId,
    }),
    shallowEqual,
  );

  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);

  const [getContractBalanceLazyQuery] = useLazyQuery(GET_CONTRACT_BALANCE, {
    variables: {
      advertiserId: seekAdvertiserId,
      advertiserNation,
    },
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
    context: {
      headers: {
        source: 'checkout',
        site: advertiserNation,
      },
    },
    onCompleted: (data) => {
      let contractType;
      if (data?.contractBalances.length > 0) {
        const hirerContract = data?.contractBalances[0];
        const contractValue =
          hirerContract?.totalValue?.dollars?.original?.value;
        const contractBalance =
          hirerContract?.totalValue?.dollars?.remaining?.value;

        dispatch(actionUpdatePaymentOption(PaymentOption.INVOICE)); // formData/paymentOption
        dispatch(
          actionUpdatePaymentMethod(
            Number(contractBalance) > 0
              ? PaymentOption.CONTRACT
              : PaymentOption.INVOICE,
          ),
        ); // tealium/paymentMethod

        dispatch(
          actionUpdateHirerContract({
            adType: hirerContract?.adType?.toLowerCase(),
            value: Number(contractValue) / 100,
            balance: Number(contractBalance) / 100,
            type: hirerContract?.contractType?.toLowerCase(),
          }),
        ); // session/hirerContract
        dispatch(actionUpdateContractBalance(contractBalance)); // session/contractBalance
        dispatch(actionUpdateHirerPurchaseType(HIRER_PURCHASE_TYPE_CONTRACT)); // tealium/hirerPurchaseType

        contractType = getContractType(data?.contractBalances);
      } else {
        dispatch(actionUpdateHirerPurchaseType(HIRER_PURCHASE_TYPE_CASUAL)); // tealium/hirerPurchaseType
      }

      dispatch(
        actionUpdateAdvertiser({
          type: contractType ?? TealiumContractType.Casual,
          pricingType: getPricingType(pricingModel),
        }),
      ); // tealium/advertiser
      setIsLoading(false);
    },
    onError: (error) => {
      log.error(
        'ContractBalance | Getting contract balance from graph failed',
        { error, advertiserNation, seekAdvertiserId },
      );
      setIsLoading(false);
    },
  });

  return { getContractBalanceLazyQuery, isLoading };
};
